import { useState, useEffect } from 'react';
import { getUserToken, isSsaEnabled } from '@cx/login';
import { TOROGOZ_API } from 'constants/urls';
import { AxiosParams } from 'models/AxiosModel';
import { useStore } from 'store/useGlobalStore';
import Config from 'config';

const isLocalDev = (): boolean => {
  const url = Config.react_app_peacock_url ?? '';
  return url.includes('localhost');
};

const getLocalDevToken = (): string | null => {
  if (!isLocalDev()) return null;

  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const tokenFile = require('../.axios_token.json');
    return tokenFile?.accessToken ?? null;
  } catch {
    return null;
  }
};

export const useAxios = (): any => {
  const [response, setResponse] = useState<any>();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [controller, setController] = useState<AbortController>();
  const isCx2Environment = useStore((state) => state.configDashboard?.isCx2Environment);

  const axiosFetch = async (params: AxiosParams) => {
    const { axiosInstance, method, url, requestConfig = {} } = params;

    // Local development (non-CX2): inject token from .axios_token.json if available
    const localDevToken = getLocalDevToken();
    if (localDevToken) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${localDevToken}`;
    } else if (isCx2Environment) {
      if (isSsaEnabled()) {
        axiosInstance.defaults.withCredentials = true;
      } else {
        const token = await getUserToken();
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
    }
    let finalUrl = url;
    const isUsingExternalApi =
      (requestConfig?.data?.external || requestConfig?.data?.External) &&
      !(url.indexOf('data/single-endpoint') >= 0);

    const isFullURL = url.startsWith('http') || url.startsWith('https');
    axiosInstance.defaults.baseURL = isUsingExternalApi === true ? '' : TOROGOZ_API;
    if (isFullURL && !isUsingExternalApi && url.indexOf('GetWidgetDataV2') >= 0) {
      finalUrl = 'DiyDashboard/GetWidgetData';
    }
    setResponse(undefined);
    try {
      setLoading(true);
      const ctrl: AbortController = new AbortController();
      setController(ctrl);
      const res = await axiosInstance[method](finalUrl, {
        ...requestConfig,
        signal: ctrl.signal,
      });
      if (res?.data) {
        setResponse(() => res?.data);
      }
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (response) setLoading(false);
  }, [response]);

  useEffect(() => {
    return () => controller && controller.abort();
  }, [controller]);
  return { response, axiosFetch, error, loading };
};
